import React, { useEffect, useState } from 'react'

import { formatPriceText, ProductV2 } from '@common'
import cx from 'classnames'
import { useTranslations } from 'next-intl'

import DynamicImage from '@/components/DynamicImage'
import { Spacer } from '@/components/Spacer'
import { useLocale } from '@/features/locale/useLocale'
import { useIntlMessageFormat } from '@/localization/useIntlMessageFormat'
import { gtmProductView } from '@/utils/gtm'
import { getProductCurrencyType, isProductOffer, isSameProductType, isSeasonPass } from '@/utils/products'
import { resolveProductImage } from '@/utils/resolveProductImage'
import { useAppSelector } from '@/utils/useAppSelector'

import { Clock } from '../../components/Animations/Clock'
import { useTranslatable } from '../../localization/useTranslatable'
import { getTimeLeftCopyKeys } from '../../utils/timer'
import { useGames } from '../common/useGames'
import { resolveRibbonKey, useProducts } from '../common/useProducts'
import { BuyButton } from './BuyButton'
import { ItemRow } from './ItemRow'
import ProductCard from './ProductCard'
import ProductDescription from './ProductDescription'
import { ProductHeader } from './ProductHeader'
import { useTimedOffer } from './useTimedOffer'

import SCIDLogo from 'assets/images/logos/supercell-id-white.svg'

import styles from './Product.module.scss'

interface ProductProps {
  product: ProductV2
  initialProductCopy: ProductCopy | null
  isProductPage: boolean
}

export interface ProductCopy {
  description: string
  contents: {
    image: string
    title: string
    description: string
  }[]
  bottomTitle: string
  bottomContent: {
    image: string
    title: string
    description: string
  }[]
}

/**
 * Order in "more from game – products"
 * 1. same product type offers
 * 2. other product type offers
 * 3. same product type
 * 4. rest
 */
const sortMoreProductsFromGame = (products: ProductV2[], product: ProductV2) => {
  const sameGameProducts = products.filter((p) => p.game === product.game && p.id !== product.id)

  const sortedProducts = sameGameProducts.sort((a, b) => {
    const aIsSameType = isSameProductType(a, product)
    const bIsSameType = isSameProductType(b, product)
    const aIsOffer = isProductOffer(a)
    const bIsOffer = isProductOffer(b)

    if (aIsSameType && !bIsSameType) {
      return -1
    } else if (!aIsSameType && bIsSameType) {
      return 1
    } else if (aIsOffer && !bIsOffer) {
      return -1
    } else if (!aIsOffer && bIsOffer) {
      return 1
    } else {
      const A = Object.values(a.prices)[0]
      const B = Object.values(b.prices)[0]
      if (A && B) {
        return B - A
      }
      return 0
    }
  })

  const sameTypeProducts = sortedProducts.filter((p) => isSameProductType(p, product)).slice(0, 3)
  const restOfProducts = sortedProducts
    .filter((p) => !isSameProductType(p, product))
    .slice(0, 3 - sameTypeProducts.length)

  return [...sameTypeProducts, ...restOfProducts]
}

export const loadProductCopy = async (product: ProductV2, locale: string): Promise<ProductCopy | null> => {
  if (!product.offerData) return null
  try {
    return (await import(`../../localization/products/${product.id}/${locale}.json`)).default
  } catch (err) {
    return null
  }
}

export const Product: React.FC<ProductProps> = ({ product, initialProductCopy, isProductPage }) => {
  const market = useAppSelector((state) => state.config.market)
  const locale = useLocale()
  // Currency is not available if country is not supported
  const currencyCountry = market?.currencyCountry
  const currency = market?.currency

  const games = useGames()
  const t = useTranslations()
  const translatable = useTranslatable()
  const intlMessageFormat = useIntlMessageFormat()
  const viewerCountry = useAppSelector((state) => state.config.viewerCountry)
  const [productCopy, setProductCopy] = useState<ProductCopy | null>(initialProductCopy)
  const { products } = useProducts()
  useEffect(() => {
    const initialiseProductCopy = async () => {
      setProductCopy(await loadProductCopy(product, locale))
    }
    initialiseProductCopy()
  }, [locale, product, productCopy])

  useEffect(() => {
    gtmProductView(product)
  }, [locale, product, viewerCountry])

  const game = games.find((g) => g.slug === product.game)

  const ribbonData = resolveRibbonKey(product.offerData)
  const ribbonContent = ribbonData ? t(ribbonData[0], ribbonData[1]) : undefined

  const { isTimedOffer, offerExpired, timeLeftText } = useTimedOffer(product)

  if (!game) {
    return <>Product not found</>
  }

  const productExtraDescriptionCopy = isSeasonPass(product)
    ? intlMessageFormat(productCopy?.description)
    : t(`${game.shortSlug}_${getProductCurrencyType(product)?.toLowerCase()}_description_1`)

  const showOriginalPrice = product.offerData?.isDiscount && currencyCountry && currency
  const originalPrice = product.offerData && 'originalPrice' in product?.offerData && product.offerData.originalPrice

  return (
    <div className={styles.Product}>
      <div
        className={cx(styles.ProductHeaderContainer, {
          [styles.InOverlayWithTimer]: isTimedOffer && isProductPage === false,
        })}
      >
        {isTimedOffer && (
          <div className={styles.TimerContainer}>
            <Clock />
            <p>
              {t('hero_pb_ct')}
              &nbsp;
              {timeLeftText.map((time, idx) => (
                <span key={idx}>{`${time}${t(getTimeLeftCopyKeys('mobile', idx))}`}&nbsp;</span>
              ))}
            </p>
          </div>
        )}
        <ProductHeader
          game={game}
          productImage={resolveProductImage(product)}
          alt={translatable(product.title)}
          size={'Large'}
          ribbonContent={ribbonContent}
          className={styles.ProductHeader}
        />
      </div>
      <div
        className={cx(styles.Content, {
          [styles.IsPopup]: isProductPage === false,
        })}
      >
        <div className={styles.MainContent}>
          <div className={styles.ProductContents}>
            <h1 className={styles.Title}>{translatable(product.title)}</h1>
            <ProductDescription product={product} tag="h2" className={styles.Description} align="left" />
          </div>
          <div className={styles.Action}>
            <BuyButton
              disabled={isTimedOffer && offerExpired}
              additionalClasses={styles.BuyButton}
              type="Primary"
              product={product}
              content={currencyCountry ? formatPriceText(product.prices[currencyCountry], locale, currency) : ''}
              isLocked={!market}
              data-cy={`product-${product.id}`}
            />
            {showOriginalPrice && originalPrice && (
              <p className={styles.OriginalPrice}>
                {formatPriceText(originalPrice[currencyCountry], locale, currency)}
              </p>
            )}
          </div>
        </div>
        <p className={styles.ExtraDescription}>{productExtraDescriptionCopy}</p>
        {isSeasonPass(product) ? (
          <div className={styles.ProductList}>
            {productCopy?.contents.map((content, idx) => {
              return (
                <ItemRow
                  key={idx}
                  title={content.title}
                  image={content.image}
                  description={intlMessageFormat(content.description)}
                  rowIndex={idx}
                />
              )
            })}
          </div>
        ) : (
          <p className={styles.ExtraDescription}>
            {game.slug !== 'clashroyale' &&
              t(`${game.shortSlug}_${getProductCurrencyType(product)?.toLowerCase()}_description_2`)}
          </p>
        )}
      </div>
      {isSeasonPass(product) && productCopy && productCopy?.bottomContent && (
        <>
          <hr className={styles.Divider} />
          <div className={styles.Footer}>
            <h1>{productCopy?.bottomTitle}</h1>
            <Spacer />
            <div className={styles.FooterContent}>
              {productCopy?.bottomContent.map((element, idx) => (
                <div key={idx} className={styles.FooterContentRow}>
                  <div className={styles.FooterContentRowImage}>
                    <DynamicImage
                      src={element.image}
                      alt={'card-image'}
                      fallback={SCIDLogo}
                      layout="fill"
                      objectFit="contain"
                    />
                  </div>
                  <div className={styles.FooterContentRowText}>
                    <h2> {element.title}</h2>
                    <p>{element.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
      <hr className={styles.Divider} />
      <div className={styles.MoreFromGame}>
        <h2>{t(`${game.shortSlug}_prod_related_prods`)}</h2>
        <div className={styles.Grid} data-cy="more-from-game-grid">
          {sortMoreProductsFromGame(products, product).map((product) => (
            <ProductCard
              type={'Normal'}
              game={game}
              key={product.id}
              product={product}
              openAs={isProductPage ? 'page' : 'overlay'}
            />
          ))}
        </div>
      </div>
    </div>
  )
}
